"use client"

import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import {
  X,
  Play,
  RotateCcw,
  Clock,
  Target,
  Zap,
  ArrowRight,
  ArrowLeft,
  Brain,
  MousePointer,
  Sparkles,
  CheckCircle2,
  GraduationCap,
} from "lucide-react"

const GuidePointer = ({ className = "" }) => {
  return (
    <div
      className={`absolute z-[1000] pointer-events-none ${className}`}
      style={{
        animation: "guide-pulse 2.5s ease-in-out infinite",
      }}
    >
      <div className="relative">
        <MousePointer
          className="w-8 h-8 text-primary drop-shadow-xl transform -rotate-45 filter drop-shadow-lg"
          fill="currentColor"
        />
        <div className="absolute -top-1 -left-1 w-10 h-10 bg-primary/20 rounded-full animate-ping" />
      </div>
    </div>
  )
}

const FloatingParticles = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(6)].map((_, i) => (
        <div
          key={i}
          className="absolute w-2 h-2 bg-primary/10 rounded-full"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animation: `float-particle ${3 + Math.random() * 4}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 2}s`,
          }}
        />
      ))}
    </div>
  )
}

const VocabularyGuide = ({ isOpen, onClose }) => {
  const { t } = useTranslation()
  const [currentStep, setCurrentStep] = useState(0)
  const [isCardFlipped, setIsCardFlipped] = useState(false)
  const [selectedRatingForDemo, setSelectedRatingForDemo] = useState(null)
  const [showPointer, setShowPointer] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const [dontShowAgain, setDontShowAgain] = useState(false)

  // Example vocabulary card for the demonstration
  const demoCard = {
    front: "der Hund",
    back: t("vocabulary.guide.demo.translation", "the dog"),
    example: {
      german: "Der Hund bellt laut.",
      english: t("vocabulary.guide.demo.exampleTranslation", "The dog barks loudly."),
    },
    pronunciation: "/hʊnt/",
  }

  const tutorialSteps = [
    {
      id: "welcome",
      title: t("vocabulary.guide.welcome.title"),
      description: t("vocabulary.guide.welcome.description"),
      showPointer: false,
      icon: GraduationCap,
      color: "text-primary",
    },
    {
      id: "the-card",
      title: t("vocabulary.guide.cardBasics.title"),
      description: t("vocabulary.guide.cardBasics.description"),
      showPointer: true,
      pointerTarget: "card",
      icon: Brain,
      color: "text-blue-500",
    },
    {
      id: "the-rating",
      title: t("vocabulary.guide.ratingSystem.title"),
      description: t("vocabulary.guide.ratingSystem.description"),
      showPointer: true,
      pointerTarget: "rating",
      icon: Target,
      color: "text-green-500",
    },
    {
      id: "srs-logic",
      title: t("vocabulary.guide.srsExplanation.title"),
      description: t("vocabulary.guide.srsExplanation.description"),
      showPointer: false,
      icon: Zap,
      color: "text-yellow-500",
    },
    {
      id: "srs-example",
      title: t("vocabulary.guide.timeline.title"),
      description: t("vocabulary.guide.timeline.description"),
      showPointer: false,
      icon: Clock,
      color: "text-purple-500",
    },
    {
      id: "ready",
      title: t("vocabulary.guide.practiceTip.title"),
      description: t("vocabulary.guide.practiceTip.description"),
      showPointer: false,
      icon: CheckCircle2,
      color: "text-emerald-500",
    },
  ]

  const currentStepData = tutorialSteps[currentStep]

  // MOBILE FIX: Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      document.body.classList.add('modal-open')

      return () => {
        document.body.style.overflow = originalOverflow
        document.body.classList.remove('modal-open')
      }
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    setIsAnimating(true)
    setIsCardFlipped(false)
    setShowPointer(false)
    setSelectedRatingForDemo(null)

    const animationTimer = setTimeout(() => setIsAnimating(false), 600)

    const pointerTimer = setTimeout(() => {
      if (tutorialSteps[currentStep].showPointer) {
        setShowPointer(true)
      }
    }, 1200)

    const cardFlipTimer = setTimeout(() => {
      if (tutorialSteps[currentStep].id === "the-card") {
        setIsCardFlipped(true)
      }
    }, 3000)

    return () => {
      clearTimeout(animationTimer)
      clearTimeout(pointerTimer)
      clearTimeout(cardFlipTimer)
    }
  }, [currentStep, isOpen])

  const handleCardFlip = () => {
    setIsCardFlipped(!isCardFlipped)
  }

  const handleRatingDemo = (rating) => {
    setSelectedRatingForDemo(rating)
  }

  const nextStep = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setIsAnimating(true)
      setTimeout(() => {
        setCurrentStep((prev) => prev + 1)
      }, 150)
    }
  }

  const prevStep = () => {
    if (currentStep > 0) {
      setIsAnimating(true)
      setTimeout(() => {
        setCurrentStep((prev) => prev - 1)
      }, 150)
    }
  }

  const handleClose = () => {
    setTimeout(() => setCurrentStep(0), 500)
    onClose(dontShowAgain)
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose()
    }
  }

  if (!isOpen) return null

  return (
    <>
      <style>{`
        @keyframes modal-entrance {
          from { 
            opacity: 0; 
            transform: scale(0.9) translateY(20px);
            filter: blur(4px);
          }
          to { 
            opacity: 1; 
            transform: scale(1) translateY(0);
            filter: blur(0px);
          }
        }
        @keyframes content-slide-in {
          from { 
            opacity: 0; 
            transform: translateY(30px) scale(0.95);
          }
          to { 
            opacity: 1; 
            transform: translateY(0) scale(1);
          }
        }
        @keyframes guide-pulse {
          0%, 100% { 
            transform: scale(1) rotate(-45deg); 
            opacity: 0.8; 
          }
          50% { 
            transform: scale(1.15) rotate(-45deg); 
            opacity: 1; 
            filter: drop-shadow(0 0 8px rgba(99, 102, 241, 0.4));
          }
        }
        @keyframes float-particle {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg); 
            opacity: 0.3;
          }
          50% { 
            transform: translateY(-20px) rotate(180deg); 
            opacity: 0.8;
          }
        }
        .modal-entrance {
          animation: modal-entrance 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
        .content-slide {
          animation: content-slide-in 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }

        /* MOBILE FIX: Prevent body scroll */
        body.modal-open {
          overflow: hidden;
          position: fixed;
          width: 100%;
        }

        /* MOBILE FIX: iOS smooth scrolling */
        .overflow-y-auto {
          -webkit-overflow-scrolling: touch;
        }
      `}</style>

      {/* MOBILE: bottom-sheet on small screens, centered on larger; safe area */}
      <div
        className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-md p-0 sm:p-4 pb-16 sm:pb-4"
        onClick={handleBackdropClick}
      >
        <FloatingParticles />

        {/* MOBILE: rounded-t-3xl on mobile, full rounded on desktop; min-h-0 for flex scroll */}
        <div
          className="relative bg-card border border-border rounded-t-3xl sm:rounded-3xl shadow-2xl w-full mx-auto flex flex-col modal-entrance overflow-hidden
                     max-w-5xl max-h-[92vh] sm:max-h-[95vh] min-h-0"
          onClick={(e) => e.stopPropagation()}
        >
          {/* MOBILE FIX: Responsive padding and improved header */}
          <div className="relative p-4 sm:p-6 md:p-8 border-b border-border bg-gradient-to-r from-card to-muted flex-shrink-0">
            {/* MOBILE: 48px touch target for close button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 sm:top-6 sm:right-6 p-2.5 rounded-xl text-muted-foreground hover:text-foreground hover:bg-background/80 transition-all duration-300 hover:rotate-90 hover:scale-110 backdrop-blur-sm z-50 touch-manipulation flex items-center justify-center min-h-[48px] min-w-[48px]"
              aria-label="Close guide"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>

            {/* MOBILE FIX: Responsive text and padding to avoid close button */}
            <div className="text-center pr-12 sm:pr-0">
              <div className="flex items-center justify-center gap-3 sm:gap-4 mb-2 sm:mb-3">
                <div className="p-2 sm:p-3 rounded-2xl bg-primary/10 backdrop-blur-sm">
                  <currentStepData.icon className={`w-8 h-8 sm:w-10 sm:h-10 ${currentStepData.color}`} />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground tracking-tight">
                  {t("vocabulary.guide.title")}
                </h2>
              </div>
              <p className="text-sm sm:text-base md:text-lg text-muted-foreground font-medium">
                {t("vocabulary.guide.subtitle")}
              </p>
            </div>

            {/* Progress bar */}
            <div className="mt-4 sm:mt-6 w-full bg-secondary rounded-full h-2 sm:h-3 overflow-hidden shadow-inner">
              <div
                className="h-full bg-gradient-to-r from-primary to-primary/80 rounded-full transition-all duration-700 ease-out shadow-sm"
                style={{
                  width: `${((currentStep + 1) / tutorialSteps.length) * 100}%`,
                  boxShadow: "0 0 10px rgba(99, 102, 241, 0.3)",
                }}
              />
            </div>
            <div className="flex justify-between mt-2 text-xs sm:text-sm text-muted-foreground px-1">
              <span>{t("lesson.header.step")} {currentStep + 1}</span>
              <span>{tutorialSteps.length} {t("lesson.header.steps")}</span>
            </div>
          </div>

          {/* MOBILE FIX: Flexible content area with proper scrolling */}
          <div className="p-4 sm:p-6 md:p-8 lg:p-10 flex-1 overflow-y-auto" style={{ minHeight: "200px" }}>
            <div
              className={`content-slide ${isAnimating ? "opacity-50" : "opacity-100"} transition-opacity duration-300`}
            >
              <div className="text-center mb-6 sm:mb-8">
                <div className="flex items-center justify-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <currentStepData.icon className={`w-6 h-6 sm:w-8 sm:h-8 ${currentStepData.color}`} />
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground tracking-tight">
                    {currentStepData.title}
                  </h3>
                </div>
                <p className="text-sm sm:text-base md:text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed px-2">
                  {currentStepData.description}
                </p>
              </div>

              <div className="flex justify-center items-center mb-6 sm:mb-8 min-h-[250px] sm:min-h-[300px] relative">
                {currentStepData.id === "welcome" && (
                  <div className="text-center space-y-4 sm:space-y-6">
                    <div className="relative">
                      <Sparkles className="w-24 h-24 sm:w-32 sm:h-32 text-primary mx-auto opacity-90 animate-pulse" />
                      <div className="absolute inset-0 w-24 h-24 sm:w-32 sm:h-32 mx-auto bg-primary/10 rounded-full animate-ping" />
                    </div>
                    <div className="space-y-2">
                      <p className="text-2xl sm:text-3xl font-bold text-foreground">
                        {t("vocabulary.guide.welcome.ready")}
                      </p>
                      <p className="text-base sm:text-lg text-muted-foreground">
                        {t("vocabulary.guide.welcome.subtitle")}
                      </p>
                    </div>
                  </div>
                )}

                {currentStepData.id === "the-card" && (
                  <div className="relative w-full max-w-sm sm:max-w-md md:max-w-lg">
                    <div style={{ perspective: "1500px" }}>
                      <div
                        className="relative w-full aspect-[3/2] transition-all duration-1000 hover:scale-105"
                        style={{
                          transformStyle: "preserve-3d",
                          transform: isCardFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
                        }}
                      >
                        {/* FRONT FACE */}
                        <div
                          onClick={handleCardFlip}
                          className="absolute inset-0 bg-card border-2 border-border rounded-2xl shadow-xl flex flex-col items-center justify-center p-6 sm:p-8 backdrop-blur-sm cursor-pointer"
                          style={{ backfaceVisibility: "hidden" }}
                        >
                          <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-2 sm:mb-3">
                            {demoCard.front}
                          </p>
                          <p className="text-lg sm:text-xl text-muted-foreground mb-3 sm:mb-4">
                            {demoCard.pronunciation}
                          </p>
                          <div className="absolute bottom-3 sm:bottom-4 flex items-center gap-2 text-xs sm:text-sm text-muted-foreground bg-muted/50 px-2 sm:px-3 py-1 rounded-full">
                            <MousePointer className="w-3 h-3 sm:w-4 sm:h-4" />
                            {t("vocabulary.guide.clickToFlip", "Click to flip")}
                          </div>
                        </div>

                        {/* BACK FACE */}
                        <div
                          onClick={handleCardFlip}
                          className="absolute inset-0 bg-card border-2 border-primary/20 rounded-2xl shadow-xl flex flex-col items-center justify-center p-6 sm:p-8 backdrop-blur-sm cursor-pointer"
                          style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
                        >
                          <p className="text-3xl sm:text-4xl font-bold text-primary mb-3 sm:mb-4">
                            {demoCard.back}
                          </p>
                          <div className="bg-muted/80 p-3 sm:p-4 rounded-xl w-full backdrop-blur-sm">
                            <p className="text-sm sm:text-base text-foreground font-medium text-center">
                              {demoCard.example.german}
                            </p>
                            <p className="text-xs sm:text-sm text-muted-foreground italic text-center mt-2">
                              {demoCard.example.english}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {currentStepData.id === "the-rating" && (
                  <div className="w-full max-w-lg relative px-2">
                    <div className="mb-4 sm:mb-6 text-center bg-muted/50 p-4 sm:p-6 rounded-2xl backdrop-blur-sm border border-border">
                      <p className="text-base sm:text-lg font-semibold text-muted-foreground mb-2">
                        {t("vocabulary.guide.rateThis")}
                      </p>
                      <p className="text-2xl sm:text-3xl font-bold text-foreground">
                        {demoCard.front} → {demoCard.back}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
                      {[
                        { label: "Again", color: "bg-red-500 hover:bg-red-600", ring: "focus:ring-red-500/30" },
                        { label: "Hard", color: "bg-orange-500 hover:bg-orange-600", ring: "focus:ring-orange-500/30" },
                        { label: "Good", color: "bg-blue-500 hover:bg-blue-600", ring: "focus:ring-blue-500/30" },
                        { label: "Easy", color: "bg-green-500 hover:bg-green-600", ring: "focus:ring-green-500/30" },
                      ].map((btn) => (
                        <button
                          key={btn.label}
                          className={`p-3 sm:p-4 rounded-xl font-semibold text-sm sm:text-base text-white transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 focus:outline-none focus:ring-4 ${btn.color} ${btn.ring} shadow-lg hover:shadow-xl touch-manipulation`}
                          style={{ minHeight: '48px' }}
                        >
                          {btn.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {currentStepData.id === "srs-logic" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-4xl w-full px-2">
                    {[
                      {
                        rating: "Again",
                        color: "red",
                        desc: t("vocabulary.guide.rating.again.explanation"),
                        icon: RotateCcw,
                      },
                      {
                        rating: "Hard",
                        color: "orange",
                        desc: t("vocabulary.guide.rating.hard.explanation"),
                        icon: Clock,
                      },
                      {
                        rating: "Good",
                        color: "blue",
                        desc: t("vocabulary.guide.rating.good.explanation"),
                        icon: Target,
                      },
                      {
                        rating: "Easy",
                        color: "green",
                        desc: t("vocabulary.guide.rating.easy.explanation"),
                        icon: Zap,
                      },
                    ].map((item) => (
                      <div
                        key={item.rating}
                        className={`p-4 sm:p-6 rounded-2xl border-l-4 bg-card/50 backdrop-blur-sm border-${item.color}-500 hover:bg-card transition-all duration-300 hover:scale-105`}
                      >
                        <div className="flex items-center gap-3 sm:gap-4 mb-2 sm:mb-3">
                          <div className={`p-2 rounded-lg bg-${item.color}-500/10`}>
                            <item.icon className={`w-5 h-5 sm:w-6 sm:h-6 text-${item.color}-500`} />
                          </div>
                          <h4 className="font-bold text-lg sm:text-xl text-foreground">{item.rating}</h4>
                        </div>
                        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                )}

                {currentStepData.id === "srs-example" && (
                  <div className="w-full max-w-3xl px-2">
                    <div className="space-y-3 sm:space-y-4">
                      {[
                        {
                          label: t("vocabulary.guide.timeline.firstTry", "First try"),
                          rating: "Good",
                          result: t("vocabulary.guide.timeline.nextReview1Day", "Next review: 1 day"),
                          color: "blue"
                        },
                        {
                          label: t("vocabulary.guide.timeline.after1Day", "After 1 day"),
                          rating: "Good",
                          result: t("vocabulary.guide.timeline.nextReview3Days", "Next review: ~3 days"),
                          color: "blue"
                        },
                        {
                          label: t("vocabulary.guide.timeline.after3Days", "After 3 days"),
                          rating: "Easy",
                          result: t("vocabulary.guide.timeline.nextReview10Days", "Next review: ~10 days (Easy bonus!)"),
                          color: "green",
                        },
                        {
                          label: t("vocabulary.guide.timeline.after10Days", "After 10 days"),
                          rating: "Hard",
                          result: t("vocabulary.guide.timeline.nextReview15Days", "Next review: ~15 days (Small increase)"),
                          color: "orange",
                        },
                        {
                          label: t("vocabulary.guide.timeline.after15Days", "After 15 days"),
                          rating: "Again",
                          result: t("vocabulary.guide.timeline.nextReview1Min", "Next review: 1 min (Lapse penalty!)"),
                          color: "red",
                        },
                      ].map((item, index) => (
                        <div
                          key={index}
                          className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 p-3 sm:p-5 bg-card/80 backdrop-blur-sm rounded-xl border border-border hover:bg-card transition-all duration-300"
                        >
                          <div className="flex items-center gap-3 sm:gap-6 flex-wrap">
                            <div className="text-xs sm:text-sm font-semibold text-muted-foreground min-w-[100px] sm:w-28">
                              {item.label}
                            </div>
                            <div
                              className={`px-3 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm font-bold rounded-full text-white bg-${item.color}-500 shadow-sm`}
                            >
                              Rated "{item.rating}"
                            </div>
                          </div>
                          <div className="text-xs sm:text-sm font-medium text-foreground">{item.result}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-muted/50 rounded-xl">
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        {t("vocabulary.guide.science.description")}
                      </p>
                    </div>
                  </div>
                )}

                {currentStepData.id === "ready" && (
                  <div className="text-center space-y-4 sm:space-y-6 px-2">
                    <div className="relative">
                      <CheckCircle2 className="w-24 h-24 sm:w-32 sm:h-32 text-emerald-500 mx-auto animate-bounce" />
                      <div className="absolute inset-0 w-24 h-24 sm:w-32 sm:h-32 mx-auto bg-emerald-500/10 rounded-full animate-ping" />
                    </div>
                    <div className="space-y-2 sm:space-y-3">
                      <p className="text-2xl sm:text-3xl font-bold text-foreground">
                        {t("vocabulary.guide.tip.title")}
                      </p>
                      <p className="text-base sm:text-lg text-muted-foreground">
                        {t("vocabulary.guide.tip.description")}
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-6 sm:mt-8 max-w-2xl mx-auto text-left">
                      <div className="p-3 sm:p-4 bg-card/50 rounded-xl border border-border">
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          ✨ {t("vocabulary.guide.success.consistency")}
                        </p>
                      </div>
                      <div className="p-3 sm:p-4 bg-card/50 rounded-xl border border-border">
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          🎯 {t("vocabulary.guide.success.honesty")}
                        </p>
                      </div>
                      <div className="p-3 sm:p-4 bg-card/50 rounded-xl border border-border">
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          🗣️ {t("vocabulary.guide.success.examples")}
                        </p>
                      </div>
                      <div className="p-3 sm:p-4 bg-card/50 rounded-xl border border-border">
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          ⏳ {t("vocabulary.guide.success.patience")}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* MOBILE FIX: Footer with guaranteed visibility and responsive buttons */}
          <div className="border-t border-border bg-muted/30 backdrop-blur-sm p-4 sm:p-6 md:p-8 flex-shrink-0">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:justify-between sm:items-center">
              {/* Previous button - MOBILE FIX: Full width on mobile with proper touch target */}
              <button
                onClick={prevStep}
                disabled={currentStep === 0}
                className="inline-flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 rounded-xl font-semibold text-sm sm:text-base text-muted-foreground bg-card border border-border hover:bg-background hover:text-foreground transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 hover:-translate-y-0.5 touch-manipulation order-1"
                style={{ minHeight: '48px' }}
              >
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">{t("vocabulary.guide.previous")}</span>
                <span className="sm:hidden">Prev</span>
              </button>

              {/* Don't show again checkbox - local state; only applied when user clicks Start Practicing */}
              <label className="flex items-center justify-center gap-2 sm:gap-3 text-xs sm:text-sm md:text-base text-muted-foreground cursor-pointer hover:text-foreground transition-colors duration-300 touch-manipulation order-3 sm:order-2 min-h-[48px]">
                <input
                  type="checkbox"
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                  className="w-5 h-5 rounded border-border text-primary focus:ring-primary/30 focus:ring-2 flex-shrink-0"
                />
                <span className="font-medium">
                  {t("vocabulary.guide.dontShowAgain")}
                </span>
              </label>

              {/* Next/Finish button - MOBILE FIX: Full width on mobile with proper touch target */}
              {currentStep < tutorialSteps.length - 1 ? (
                <button
                  onClick={nextStep}
                  className="inline-flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 rounded-xl font-semibold text-sm sm:text-base text-primary-foreground bg-primary hover:bg-primary/90 transition-all duration-300 transform hover:scale-105 hover:-translate-y-0.5 shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-primary/30 touch-manipulation order-2 sm:order-3"
                  style={{ minHeight: '48px' }}
                >
                  <span className="hidden sm:inline">{t("vocabulary.guide.next")}</span>
                  <span className="sm:hidden">Next</span>
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              ) : (
                <button
                  onClick={handleClose}
                  className="inline-flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 rounded-xl font-semibold text-sm sm:text-base text-white bg-emerald-600 hover:bg-emerald-700 transition-all duration-300 transform hover:scale-105 hover:-translate-y-0.5 shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-emerald-500/30 touch-manipulation order-2 sm:order-3"
                  style={{ minHeight: '48px' }}
                >
                  {t("vocabulary.guide.startPracticing")}
                  <Play className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default VocabularyGuide